import { Button, Grid, Snackbar, Tooltip, Typography  } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { CustomerDTO, AcademicYearDTO, ProductDTO, ProvisionTreeNodeDTO, SiteDTO } from '@beable/roster-backend-client-v1';
import Header from '../Header/Header';
import BasePanel from '../Common/Layout/BasePanel';
import { BasePanelBox } from '../Common/Layout/BasePanelBox';
import BaseSheet from '../Common/Layout/BaseSheet';
import provisionService from "../../services/ProvisionService";
import ProvisionTreeSelections from '../ProvisionTreeSelections/ProvisionTreeSelections';
import Sidebar from './Sidebar';
import { getGenericStyles } from '../../theme/styles';
import { formatLogText } from '../../util/formatLogText';
import InfoIcon from '@mui/icons-material/Info';
import rosterBackendService from '../../services/RosterBackendService';
import MuiAlert from '@mui/material/Alert';

interface MessageSettings {
  open: boolean;
  message: string;
  severity: 'error' | 'info' | 'success' | 'warning' | undefined;
}

const initialMessageSettings = {
  open: false,
  message: "",
  severity: "info" as 'error' | 'info' | 'success' | 'warning',
};
const initialCustomerValue: CustomerDTO = new CustomerDTO();
const initialSiteValue: SiteDTO = new SiteDTO();

export default function SummerClasses() {
  const theme = useTheme();
  const styles = getGenericStyles(theme);

  const [_customer, setCustomer] = useState(initialCustomerValue);
  const [site, setSite] = useState(initialSiteValue);
  const [product, setProduct] = useState<ProductDTO>();
  const [academicYear, setAcademicYear] = useState<AcademicYearDTO>();
  const [loadingProgress, setLoadingProgress] = useState('');
  const [rosterWarning, setRosterWarning] = useState("");
  const [nodes, setNodes] = useState<any[]>([]);
  const [checkedNodes, setCheckedNodes] = useState<any[]>([]);
  const [preSelectedNodes, setPreSelectedNodes] = useState<any[]>([]);
  const [logInfo, _setLogInfo] = useState<any>();
  const [isNodesLoading, setIsNodesLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messageSettings, setMessageSettings] = useState<MessageSettings>(initialMessageSettings);

  useEffect(() => {
    const fetchProvisionNodes = async () => {
      const roots = await provisionService.getProvisionNodes(site.id, academicYear!.academicYearId);
      let data;

      if(roots?.length) {
        const progressUnit = 100/roots.length;
        let progress = 0;
        for(const root of roots) {
          const rootWithChildren = await provisionService.getRootNode(site.id, academicYear!.academicYearId, root.value, true);
          root.children = rootWithChildren?.children;
          progress += progressUnit;
          setLoadingProgress(`${progress.toFixed(2)}%`);
        }
        data = roots;
      } 
      setLoadingProgress('');

      if (data && data[0] && data[0].warning) {
        setRosterWarning(data[0].warning);
      } else if (!data) {
        setRosterWarning('Error while getting the provision tree');
      } else if (data) {
        setNodes(data);
        setRosterWarning('');
      }
    };

    const fetchClassMetadataSelections = async () => {
      const selections = await rosterBackendService.getClassMetadataSelections({ siteId: site.id, academicYearId: academicYear!.academicYearId, productId: product!.id });
      if (selections.isOk()) {
        if (Array.isArray(selections)) {
          setCheckedNodes(selections);
          setPreSelectedNodes(selections);
          return;
        }
        setCheckedNodes(selections.value);
        setPreSelectedNodes(selections.value);
      }
    };

    const fetchData = async () => {
      setIsNodesLoading(true);
      await fetchProvisionNodes();
      await fetchClassMetadataSelections();
      setIsNodesLoading(false);
    }

    if (site?.id && academicYear?.academicYearId && product?.id) {
      fetchData();
    }
  }, [site, academicYear, product]);

  useEffect(() => {
    closeMessage();
  }, [checkedNodes, preSelectedNodes]);

  const handleCustomerChange = (customer: CustomerDTO) => {
    clearNodes();
    setSite(initialSiteValue);
    setAcademicYear(undefined);
    setCustomer(customer);
  };

  const handleSiteChange = async (site: SiteDTO) => {
    clearNodes();
    setAcademicYear(undefined);
    setSite(site || initialSiteValue);
  }

  const clearNodes = () => {
    setNodes([]);
    setCheckedNodes([]);
    setPreSelectedNodes([]);
  }

  const handleProductChange = (product: ProductDTO) => {
    clearNodes();
    setProduct(product);
  }

  const handleAcademicYearChange = (academicYear: AcademicYearDTO | undefined) => {
    clearNodes();
    setAcademicYear(academicYear);
  }
  
  async function getNodeChildren(node: ProvisionTreeNodeDTO) {
    const data = await provisionService.getProvisionNodes(site.id, academicYear!.academicYearId, node.value);
    return data;
  }
  
  const handleNodeCheck = (data: any[]) => {
    setCheckedNodes(data);
    setPreSelectedNodes(data);
  };

  const handleSelectionsChange = (newSelectedNodes: string[]) => {
    handleNodeCheck([...newSelectedNodes]);
  }
 
  async function getRootNode(node: ProvisionTreeNodeDTO, appendChildren?: boolean) {
    const data = await provisionService.getRootNode(site.id, academicYear!.academicYearId, node.value, appendChildren);
    return data;
  }

  const handleSubmit = () => {
    const submitData = async () => {
      try {
        setIsLoading(true);
        const response = await rosterBackendService.saveClassMetadataSelections({ siteId: site.id, academicYearId: academicYear!.academicYearId, productId: product!.id, selections: checkedNodes })
        setIsLoading(false);

        if (response.isOk() && response.value) {
          displaySuccess('Summer class selections saved successfully');
        } else if (response.isErr()) {
          displayError(`Error while saving summer class selections. Error: ${response.error.message}`);
        } else {
          displayError(`Error while saving summer class selections. Error: ${response}`);
        }
      } catch (error) {
        setIsLoading(false);
        displayError(`Error while saving summer class selections. Error: ${error}`);
      }
    };

    if (
      academicYear &&
      academicYear.schoolYear >= new Date(Date.now()).getFullYear()
    ) {
      submitData();
    } else {
      displayWarning('You cannot save summer class selections for this academic year. It has ended.');
    }
  };

  const Message = () => {
    return (
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={messageSettings.open && !isLoading}
        autoHideDuration={5000}
        onClose={closeMessage}
        sx={{ position: 'absolute' }}
      >
        <MuiAlert
          severity={messageSettings.severity}
          // elevation={6}
          variant="filled"
        >
          {messageSettings.message}
        </MuiAlert>
      </Snackbar>
    )
  }
  
  const closeMessage = () => {
    setMessageSettings({ ...initialMessageSettings });
  };
  
  const displayMessage = (message: string, severity: 'error' | 'info' | 'success' | 'warning') => {
    setMessageSettings({
      open: true,
      message,
      severity,
    });
  };
  
  const displayError = (message: string) => {
    displayMessage(message, 'error');
  };

  const displaySuccess = (message: string) => {
    displayMessage(message, 'success');
  };

  const displayWarning = (message: string) => {
    displayMessage(message, 'warning');
  };
  
  const summerClassSelections = (
    <>
      <Typography variant="h6" sx={styles.stepContainer_header}>
        Summer Classes
        {logInfo && <Tooltip title={formatLogText(logInfo)}>
          <InfoIcon color="primary" sx={styles.infoIcon} />
        </Tooltip>}
      </Typography>
      <ProvisionTreeSelections
        nodes={nodes}
        preSelectedNodeValues={preSelectedNodes}
        onSelectionsChange={(newSelectedNodes) => handleSelectionsChange(newSelectedNodes)}
        getNodeChildren={getNodeChildren}
        getRootNode={getRootNode}
        rosterWarning={rosterWarning}
        isLoading={isNodesLoading}
        loadingProgress={loadingProgress}
      />
    </>
  )

  const saveSelections = (
    <Grid
      container
      sx={{
        justifyContent: "center"
      }}
    >
      <Grid>
        <Button
          variant={"contained"}
          color={"primary"}
          onClick={() => {
            handleSubmit();
          }}
          disabled={
            !site?.id ||
            !academicYear?.academicYearId ||
            rosterWarning.length > 0 ||
            isLoading
          }
        >
          Save Selections
        </Button>
      </Grid>
    </Grid>
  )

  return (
    <>
      <Header />
      <Grid container>
        <div id='summer-classes-sidebar'>
          <BasePanel>
            <BasePanelBox>
              <Sidebar 
                title='Summer Classes'
                onCustomerChange={handleCustomerChange}
                onSiteChange={handleSiteChange}
                onProductChange={handleProductChange}
                onAcademicYearChange={handleAcademicYearChange}
              />
            </BasePanelBox>
          </BasePanel>
        </div>
        <div id='summer-classes-selections-tree'>
          <BaseSheet maxWidth="lg">
            {summerClassSelections}
            {saveSelections}
            <Message />
          </BaseSheet>
        </div>
      </Grid >
    </>
  );
}
